import { type ReactNode, useEffect, useRef } from "react"
import { cn } from "@/lib/utils"

/**
 * Slide-over panel on a native <dialog> (focus trap, Escape and inert background for free).
 * `side="right"` is the desktop slide-over; `side="bottom"` is the phone sheet; `side="center"` (wave B2, decision 0057:
 * the wide review dialog) is a centred dialog up to 1100px wide and 90% of the screen high that scrolls inside, and on a
 * phone renders as the bottom sheet. On open, focus goes to the sheet's title (`SheetHeader`, `tabIndex={-1}`), so a
 * screen reader starts at the top and the first control isn't ringed by surprise; the dialog itself draws no ring (the
 * only `outline-none` in the app, decision 0057 §5.4: the global `:focus-visible` rule is the one ring).
 */
export function Sheet({
  open,
  onOpenChange,
  title,
  side = "right",
  children,
  className,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  side?: "right" | "bottom" | "center" | undefined
  children: ReactNode
  className?: string | undefined
}) {
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    if (open && !dialog.open) {
      dialog.showModal()
      dialog.querySelector<HTMLElement>("[data-sheet-title]")?.focus()
    }
    if (!open && dialog.open) dialog.close()
  }, [open])

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: backdrop click; Escape closes the dialog natively
    <dialog
      ref={ref}
      aria-label={title}
      onClose={() => onOpenChange(false)}
      onClick={(event) => {
        if (event.target === event.currentTarget) onOpenChange(false)
      }}
      className={cn(
        "m-0 max-h-none max-w-none bg-surface p-0 text-ink shadow-sheet outline-none",
        side === "right"
          ? "ml-auto h-dvh w-[min(520px,100vw)] border-l border-rule animate-[sheet-in-right_200ms_var(--ease-minga)]"
          : side === "center"
            ? cn(
                "m-auto max-h-[90dvh] w-[min(1100px,calc(100vw-48px))] overflow-y-auto rounded-sheet border border-rule",
                "max-sm:mt-auto max-sm:mb-0 max-sm:max-h-[92dvh] max-sm:w-full max-sm:rounded-none max-sm:rounded-t-sheet",
                "max-sm:border-x-0 max-sm:border-b-0 max-sm:pt-3 max-sm:animate-[sheet-in-bottom_200ms_var(--ease-minga)]",
              )
            : "mt-auto max-h-[92dvh] w-full rounded-t-sheet border-t border-rule pt-3 animate-[sheet-in-bottom_200ms_var(--ease-minga)]",
        className,
      )}
    >
      {(side === "bottom" || side === "center") && (
        <span
          aria-hidden="true"
          className={cn(
            "absolute top-2 left-1/2 h-1 w-9 -translate-x-1/2 rounded-full bg-line-control",
            side === "center" && "sm:hidden",
          )}
        />
      )}
      {open && children}
    </dialog>
  )
}

/** Sheet header (refined-system §5.8): serif 22 title, an optional sans 13 meta slot, a quiet 16px close. */
export function SheetHeader({
  title,
  onClose,
  children,
}: {
  title: ReactNode
  onClose: () => void
  children?: ReactNode
}) {
  return (
    <div className="flex items-center gap-3 px-6 pt-5 pb-3 max-sm:px-4">
      <h2
        data-sheet-title=""
        tabIndex={-1}
        className="font-serif text-[22px] leading-[1.25] font-normal tracking-[-0.005em]"
      >
        {title}
      </h2>
      {children && <div className="type-secondary text-muted">{children}</div>}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="tap ml-auto flex size-8 items-center justify-center rounded-control text-muted hover:bg-fill hover:text-ink"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <path d="M4 4l8 8M12 4l-8 8" />
        </svg>
      </button>
    </div>
  )
}
